import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Users } from "lucide-react";

/**
 * Assign / Reassign the Design team on a project.
 *
 * Per Feb 2026 spec:
 *   - Design Manager is required.
 *   - Interior Designer is OPTIONAL. If omitted, the project stays with the
 *     Design Manager (visible on their board). If provided, the primary
 *     ownership shifts to the Interior Designer (their board).
 */
export default function AllocateTeamModal({ open, project, onClose, onSaved, endpoint = "/allocate" }) {
  const [managers, setManagers] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [managerId, setManagerId] = useState("");
  const [designerId, setDesignerId] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setManagerId(project?.design_manager_id || "");
    setDesignerId(project?.interior_designer_id || "");
    (async () => {
      try {
        const { data } = await api.get("/design/managers");
        setManagers(data);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    })();
  }, [open, project]);

  // When manager changes, load designers reporting to that manager
  useEffect(() => {
    if (!managerId) { setDesigners([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/design/designers?manager_id=${managerId}`);
        setDesigners(data);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    })();
  }, [managerId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!managerId) { setErr("Design Manager is required"); return; }
    setSaving(true);
    try {
      await api.post(`/design/projects/${project.id}${endpoint}`, {
        design_manager_id: managerId,
        interior_designer_id: designerId || null,
      });
      toast.success(designerId ? "Team assigned" : "Design Manager assigned");
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !project) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="allocate-modal">
      <div className="w-full max-w-md iz-card p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
            <Users className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5}/>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Allocate</p>
            <h2 className="font-display text-xl text-white">Assign design team</h2>
            <p className="text-[11px] text-gray-500 font-mono">{project.project_name}</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Design Manager *</label>
            <select value={managerId} onChange={(e) => { setManagerId(e.target.value); setDesignerId(""); }} className="iz-input" required data-testid="allocate-manager">
              <option value="">Select manager…</option>
              {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Interior Designer (optional)</label>
            <select value={designerId} onChange={(e) => setDesignerId(e.target.value)} className="iz-input" disabled={!managerId} data-testid="allocate-designer">
              <option value="">{managerId ? "— Leave with Design Manager —" : "Pick a manager first"}</option>
              {designers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <p className="text-[10px] text-gray-500 mt-1">
              If skipped, the Design Manager handles the project directly (shows on their board). If picked, ownership shifts to the Interior Designer.
            </p>
          </div>

          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="allocate-submit">
              {saving ? "Assigning…" : "Assign"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
